import { useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { fmtDate, fmtTons } from "@/lib/format";
import CustomerAutocomplete from "@/components/CustomerAutocomplete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Snowflake, LogOut, CheckCircle2, Truck, WifiOff, RefreshCw, CloudUpload } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const QUEUE_KEY = "sfx_pending_sales";

const loadQueue = () => {
  try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]"); } catch { return []; }
};
const saveQueue = (q) => localStorage.setItem(QUEUE_KEY, JSON.stringify(q));

export default function LabourApp() {
  const { user, logout } = useAuth();
  const [plants, setPlants] = useState([]);
  const [recent, setRecent] = useState([]);
  const [pending, setPending] = useState(loadQueue());
  const [online, setOnline] = useState(navigator.onLine);
  const [saleDate, setSaleDate] = useState(today());
  const [customer, setCustomer] = useState("");
  const [tons, setTons] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [plant, setPlant] = useState("SFX 1");
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const flushing = useRef(false);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (!customer.trim()) { setVehicles([]); return; }
      try { const { data } = await api.get("/sales/vehicles", { params: { customer: customer.trim() } }); setVehicles(data); }
      catch (e) { setVehicles([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [customer]);

  const loadRecent = async () => {
    try { const { data } = await api.get("/sales/recent"); setRecent(data); } catch (e) {}
  };

  const flushQueue = useCallback(async (showToast = false) => {
    if (flushing.current) return;
    const q = loadQueue();
    if (q.length === 0) return;
    if (!navigator.onLine) return;
    flushing.current = true;
    setSyncing(true);
    let remaining = [...q];
    let synced = 0;
    for (const item of q) {
      try {
        await api.post("/sales", {
          sale_date: item.sale_date, customer_name: item.customer_name,
          tons: item.tons, vehicle_no: item.vehicle_no, plant: item.plant,
        });
        remaining = remaining.filter((r) => r.localId !== item.localId);
        saveQueue(remaining);
        setPending([...remaining]);
        synced += 1;
      } catch (e) {
        break; // stop on first failure (likely offline again); keep the rest queued
      }
    }
    flushing.current = false;
    setSyncing(false);
    if (synced > 0) {
      await loadRecent();
      if (showToast) toast.success(`${synced} offline ${synced === 1 ? "entry" : "entries"} synced`);
    }
  }, []);

  useEffect(() => {
    api.get("/plants").then((r) => {
      setPlants(r.data);
      if (r.data.length) setPlant(r.data[0].name);
    }).catch(() => {});
    loadRecent();
    flushQueue(true);

    const goOnline = () => { setOnline(true); flushQueue(true); };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    const interval = setInterval(() => flushQueue(false), 30000);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
      clearInterval(interval);
    };
    // eslint-disable-next-line
  }, []);

  const enqueue = (payload) => {
    const item = { ...payload, localId: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}` };
    const q = [...loadQueue(), item];
    saveQueue(q);
    setPending(q);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!customer.trim() || !tons) {
      toast.error("Enter customer and quantity");
      return;
    }
    if (saleDate > today()) {
      toast.error("Future dates are not allowed");
      return;
    }
    const payload = {
      sale_date: saleDate, customer_name: customer.trim(),
      tons: Number(tons), vehicle_no: vehicle.trim(), plant,
    };
    const resetForm = () => { setCustomer(""); setTons(""); setVehicle(""); };

    if (!navigator.onLine) {
      enqueue(payload);
      toast.success("Saved offline — will sync when back online");
      resetForm();
      return;
    }

    setSaving(true);
    try {
      await api.post("/sales", payload);
      toast.success("Sale saved");
      resetForm();
      await loadRecent();
      flushQueue(false);
    } catch (err) {
      if (!err.response) {
        // network error — queue it for later
        enqueue(payload);
        toast.success("Saved offline — will sync when back online");
        resetForm();
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      }
    } finally {
      setSaving(false);
    }
  };

  const plantStyle = (name, active) => {
    const isSfx2 = name === "SFX 2";
    if (active) {
      return isSfx2
        ? "bg-indigo-600 text-white border-indigo-600 shadow-lg"
        : "bg-sky-500 text-white border-sky-500 shadow-lg";
    }
    return "bg-white text-slate-600 border-slate-200";
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <header className="sticky top-0 z-30 bg-sky-600 text-white px-4 py-3 flex items-center justify-between shadow">
        <div className="flex items-center gap-2">
          <Snowflake className="h-6 w-6" />
          <div className="leading-tight">
            <div className="font-chivo font-extrabold text-base">SFX ICE PLANT</div>
            <div className="text-[11px] text-sky-100">{user?.name}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!online && <span className="flex items-center gap-1 text-xs bg-amber-400 text-amber-900 font-semibold px-2 py-1 rounded" data-testid="offline-badge"><WifiOff className="h-3.5 w-3.5" /> Offline</span>}
          <Button variant="ghost" size="sm" onClick={logout} data-testid="labour-logout-button" className="text-white hover:bg-sky-700">
            <LogOut className="h-5 w-5" />
          </Button>
        </div>
      </header>

      {pending.length > 0 && (
        <div className="bg-amber-50 border-b border-amber-200 text-amber-800 px-4 py-2.5 flex items-center justify-between" data-testid="pending-banner">
          <span className="flex items-center gap-2 text-sm font-medium">
            <CloudUpload className="h-4 w-4" />
            {pending.length} {pending.length === 1 ? "entry" : "entries"} waiting to sync
          </span>
          <Button size="sm" variant="ghost" className="text-amber-800 hover:bg-amber-100 h-8" onClick={() => flushQueue(true)} disabled={syncing || !online} data-testid="sync-now-button">
            <RefreshCw className={`h-4 w-4 mr-1 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing…" : "Sync now"}
          </Button>
        </div>
      )}

      <div className="max-w-xl mx-auto px-4 pt-5">
        <h1 className="font-chivo font-black text-2xl text-slate-900 mb-4">Add New Ice Sale</h1>

        <form onSubmit={submit} className="space-y-5" data-testid="add-sale-form">
          <div>
            <Label className="text-base">Date</Label>
            <Input
              type="date" data-testid="sale-date-input" max={today()}
              className="h-14 mt-1.5 text-lg"
              value={saleDate} onChange={(e) => setSaleDate(e.target.value)}
            />
          </div>

          <div>
            <Label className="text-base">Customer</Label>
            <div className="mt-1.5">
              <CustomerAutocomplete value={customer} onChange={setCustomer} testid="sale-customer-input" className="h-14 text-lg" />
            </div>
          </div>

          <div>
            <Label className="text-base">Quantity (tons)</Label>
            <Input type="number" inputMode="decimal" step="0.01" data-testid="sale-tons-input"
              className="h-14 mt-1.5 text-lg" placeholder="e.g. 10"
              value={tons} onChange={(e) => setTons(e.target.value)} />
          </div>

          <div>
            <Label className="text-base">Vehicle No.</Label>
            <Input data-testid="sale-vehicle-input" className="h-14 mt-1.5 text-lg uppercase" placeholder="MH15AB1234"
              list="veh-list" value={vehicle} onChange={(e) => setVehicle(e.target.value)} />
            <datalist id="veh-list">
              {vehicles.map((v) => <option key={v} value={v} />)}
            </datalist>
          </div>

          <div>
            <Label className="text-base">Select Plant</Label>
            <div className="grid grid-cols-2 gap-3 mt-1.5">
              {plants.map((p) => (
                <button type="button" key={p.id}
                  data-testid={`plant-select-${p.name.replace(/\s+/g, "").toLowerCase()}`}
                  onClick={() => setPlant(p.name)}
                  className={`h-20 rounded-md border-2 font-chivo font-black text-2xl tracking-wide transition-colors active:scale-[0.98] ${plantStyle(p.name, plant === p.name)}`}>
                  {p.name}
                </button>
              ))}
            </div>
          </div>

          <Button type="submit" disabled={saving} data-testid="submit-sale-button"
            className="w-full h-16 text-xl font-bold bg-emerald-600 hover:bg-emerald-700">
            {saving ? "Saving…" : "SUBMIT"}
          </Button>
        </form>

        {plants.map((p) => {
          const items = recent.filter((r) => r.plant === p.name);
          const pend = pending.filter((r) => r.plant === p.name);
          const key = p.name.replace(/\s+/g, "").toLowerCase();
          return (
            <div className="mt-8" key={p.id}>
              <h2 className="font-chivo font-bold text-lg text-slate-800 mb-3 flex items-center gap-2">
                <CheckCircle2 className={`h-5 w-5 ${p.name === "SFX 2" ? "text-indigo-600" : "text-sky-600"}`} /> Recent {p.name}
              </h2>
              <div className="space-y-2" data-testid={`recent-list-${key}`}>
                {pend.map((r) => (
                  <div key={r.localId} data-testid={`pending-item-${r.localId}`}
                    className="bg-amber-50 border border-amber-200 rounded-md p-3 flex items-center justify-between">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 truncate">{r.customer_name}</div>
                      <div className="text-xs text-amber-700 flex items-center gap-2 mt-0.5">
                        <span>{fmtDate(r.sale_date)}</span>
                        <span className="flex items-center gap-1"><CloudUpload className="h-3 w-3" /> Pending sync</span>
                      </div>
                    </div>
                    <span className="font-chivo font-bold text-slate-900 tabular ml-3">{fmtTons(r.tons)}t</span>
                  </div>
                ))}
                {items.length === 0 && pend.length === 0 && <p className="text-slate-400 text-sm">No entries yet.</p>}
                {items.map((r) => (
                  <div key={r.id} data-testid={`recent-item-${r.id}`}
                    className="bg-white border border-slate-200 rounded-md p-3 flex items-center justify-between">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 truncate">{r.customer_name}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                        <span>{fmtDate(r.sale_date)}</span>
                        {r.vehicle_no && <span className="flex items-center gap-1"><Truck className="h-3 w-3" />{r.vehicle_no}</span>}
                        {r.created_by_name && <span className="text-slate-400">· {r.created_by_name}</span>}
                      </div>
                    </div>
                    <span className="font-chivo font-bold text-slate-900 tabular ml-3">{fmtTons(r.tons)}t</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
